import React from 'react';
import QRCode from 'qrcode';
import { Smartphone, Download } from 'lucide-react';

/**
 * Small "scan to install" QR card for marketing pages.
 * Renders a QR for docsite.care (or any `url` prop) with an Install-DocSite caption.
 * Doctor shows this on a laptop / slide → a peer scans with their phone → DocSite
 * opens in the browser → native PWA install prompt appears ("Add to Home Screen").
 */
export default function InstallQR({
  url = 'https://docsite.care/',
  title = 'Scan to install DocSite',
  subtitle = 'Opens in your phone browser → tap "Install" / "Add to Home Screen"',
  variant = 'card', // 'card' | 'pill'
  className = '',
  testId = 'install-qr',
}) {
  const [dataUrl, setDataUrl] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, {
      width: 480,
      margin: 2,
      errorCorrectionLevel: 'Q',
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((d) => { if (!cancelled) setDataUrl(d); })
      .catch(() => { /* QR library rarely fails; silent no-op keeps hero clean */ });
    return () => { cancelled = true; };
  }, [url]);

  if (variant === 'pill') {
    return (
      <div className={`inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm ${className}`} data-testid={testId}>
        <div className="w-14 h-14 rounded-lg bg-white p-1 border border-slate-100 flex-shrink-0">
          {dataUrl && <img src={dataUrl} alt={title} className="w-full h-full object-contain" />}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-slate-900 leading-tight flex items-center gap-1"><Smartphone size={11} /> {title}</p>
          <p className="text-[10px] text-slate-600 leading-snug mt-0.5">{subtitle}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-stretch gap-4 rounded-2xl border border-sky-200 bg-gradient-to-br from-white via-sky-50/40 to-white p-3 pr-5 shadow-sm ${className}`}
      data-testid={testId}
    >
      <div className="rounded-xl bg-white p-2 border border-slate-200 flex-shrink-0" style={{ width: 'clamp(7rem, 9vw, 11rem)', height: 'clamp(7rem, 9vw, 11rem)' }}>
        {dataUrl ? (
          <img src={dataUrl} alt={title} className="w-full h-full object-contain" data-testid={`${testId}-image`} />
        ) : (
          <div className="w-full h-full bg-slate-100 rounded-md animate-pulse" aria-label="Loading QR code" />
        )}
      </div>
      <div className="flex flex-col justify-center min-w-0">
        <p className="text-[10px] font-bold text-sky-700 uppercase tracking-[0.18em] mb-1 flex items-center gap-1.5">
          <Download size={10} /> Install the app
        </p>
        <p className="text-sm font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</p>
        <p className="text-xs text-slate-700 leading-snug mt-1 max-w-[220px]">{subtitle}</p>
      </div>
    </div>
  );
}
